import { api } from "@/lib/apiClient";
import type { Attribute, AttributeOption } from "@/lib/catalog/types";
import { normalizeVisibilityRule } from "@/lib/catalog/attributeVisibility";

type ApiOption = {
  id: string;
  label: string;
  value: string;
  sort_order?: number;
};

type ApiAttribute = {
  id: string;
  options?: ApiOption[];
};

function mapOptions(raw: ApiOption[] | undefined): AttributeOption[] {
  return (raw ?? []).map((opt, idx) => ({
    id: String(opt.id ?? ""),
    label: String(opt.label ?? ""),
    value: String(opt.value ?? ""),
    sortOrder: Number(opt.sort_order ?? idx),
  }));
}

export async function addAttributeRuntimeOption(
  attributeId: string,
  label: string,
): Promise<{ attribute: Pick<Attribute, "id" | "options">; optionValue: string }> {
  const response = await api.post<{ data?: ApiAttribute; created_option_value?: string; message?: string }>(
    `/api/v1/catalog/attributes/${attributeId}/options`,
    { label },
  );
  const data = response.data?.data;
  if (!data) {
    throw new Error("Invalid response when adding attribute option.");
  }
  const options = mapOptions(data.options);
  const optionValue = String(response.data?.created_option_value ?? "");
  return {
    attribute: { id: String(data.id), options },
    optionValue,
  };
}

export function mergeAttributeOptionsInSchema<T extends { groups: { attributes: Attribute[] }[] }>(
  schema: T,
  attributeId: string,
  options: AttributeOption[],
): T {
  return {
    ...schema,
    groups: schema.groups.map((group) => ({
      ...group,
      attributes: group.attributes.map((attr) =>
        attr.id === attributeId ? { ...attr, options } : attr,
      ),
    })),
  };
}

export function mapAttributeFromApiRecord(raw: Record<string, unknown>): Attribute {
  const optionsRaw = raw.options;
  const options: AttributeOption[] = Array.isArray(optionsRaw)
    ? optionsRaw.map((opt, idx) => {
        const o = opt as Record<string, unknown>;
        return {
          id: String(o.id ?? ""),
          label: String(o.label ?? ""),
          value: String(o.value ?? ""),
          sortOrder: Number(o.sort_order ?? o.sortOrder ?? idx),
        };
      })
    : [];

  return {
    id: String(raw.id ?? ""),
    code: String(raw.code ?? ""),
    name: String(raw.name ?? ""),
    type: raw.type as Attribute["type"],
    isRequired: Boolean(raw.is_required ?? raw.isRequired),
    isListing: Boolean(raw.is_listing ?? raw.isListing),
    isFilter: Boolean(raw.is_filter ?? raw.isFilter),
    isInvoice: Boolean(raw.is_invoice ?? raw.isInvoice),
    isDeliveryChecklist: Boolean(raw.is_delivery_checklist ?? raw.isDeliveryChecklist),
    isGatePass: Boolean(raw.is_gate_pass ?? raw.isGatePass),
    allowRuntimeAdd: Boolean(raw.allow_runtime_add ?? raw.allowRuntimeAdd),
    sortOrder: Number(raw.sort_order ?? raw.sortOrder ?? 0),
    parentAttributeId:
      raw.parent_attribute_id == null || raw.parent_attribute_id === ""
        ? null
        : String(raw.parent_attribute_id ?? raw.parentAttributeId ?? ""),
    visibilityRule: normalizeVisibilityRule(raw.visibility_rule ?? raw.visibilityRule),
    options,
  };
}
